import { ProfileCardTitle, ProfileGrid, profileCardClasses } from 'ui/ProfileCard';
import { CalendarIcon, CaseIcon, UserIcon } from 'ui/icons';
import { CompactText } from 'ui/CompactText';
import { MapPin, Pencil, Video } from 'lucide-react';
import { PageHead } from 'ui/kit/PageHead';
import { useMemo, useState } from 'react';
import { NavLink } from 'react-router';

import { useProfileModeration } from 'hooks/useProfileModeration';
import {
  useVerificationEdits,
  type RejectedVerificationItem,
} from 'hooks/useVerificationEdits';
import { useGetCurrentProfile, useRevertProfileDraft } from 'modules/Profile/model/services';
import { addNotification } from 'notifications';
import { RichAboutContent } from 'ui/RichAboutContent';
import { hasAboutContent } from 'utils/aboutHtml';
import {
  markProfilePhotoFailed,
  profilePhotoFallback,
  profilePhotoSrc,
} from 'utils/profilePhotoSrc';
import { sexShortLabel } from 'utils/sexLabels';
import { SectionReveal } from 'ui/kit/uiMotion';

import { TutorDailyResponsesCard } from './TutorDailyResponsesCard';
import { TutorReviewsSection } from './TutorReviewsSection';
import { TutorReviewsSummary } from './TutorReviewsSummary';
import styles from './TeacherProfile.module.scss';

type TeacherProfileProps = {
  StudentView?: boolean;
};

const ITEM_LABELS: Record<string, string> = {
  profile_photo: 'Фото профиля',
  profile_data: 'О себе',
};

const itemTitle = (item: Pick<RejectedVerificationItem, "kind">) => {
  if (item.kind === 'education') {
    return 'Период обучения';
  }
  return ITEM_LABELS[item.kind] ?? item.kind;
};

const EDU_FIELDS = new Set([
  'university',
  'faculty',
  'specialization',
  'years',
  'diploma_photo',
  'education',
  'teacher_educations',
]);
const SERVICE_FIELDS = new Set([
  'category',
  'subcategory',
  'price',
  'service',
  'services',
  'teacher_prices',
]);

const PendingNote = ({ show, to }: { show: boolean; to: string }) =>
  show ? (
    <p className={styles.pendingNote}>
      После проверки появится этот вариант.{' '}
      <NavLink to={to}>Изменить и отправить снова</NavLink>
    </p>
  ) : null;

const formatPrice = (price: number | string | null | undefined) => {
  const n = Number(price);
  if (!Number.isFinite(n)) return '';
  return `${n.toLocaleString('ru-RU')} руб. / час`;
};

export const TeacherProfile = ({ StudentView = false }: TeacherProfileProps) => {
  const { data } = useGetCurrentProfile();
  const { status, comment } = useProfileModeration();
  const { rejectedItems, declinedChanges, pendingEdits, canRevert, draftChanges } =
    useVerificationEdits();
  const revertDraft = useRevertProfileDraft();
  const [photoFailed, setPhotoFailed] = useState(false);

  const fullname = [data?.surname, data?.name, data?.patronymic].filter(Boolean).join(' ');
  const photoPath = useMemo(() => {
    if (photoFailed) return profilePhotoFallback();
    return profilePhotoSrc(data?.profile_photo_token) || profilePhotoFallback();
  }, [data?.profile_photo_token, photoFailed]);

  const subject =
    data?.teacher_prices?.find((row) => row.category_title || row.category_name)
      ?.category_title ||
    data?.teacher_prices?.find((row) => row.category_title || row.category_name)
      ?.category_name ||
    'математике';
  const sex = sexShortLabel(data?.sex);
  const pendingFields = useMemo(
    () => new Set(draftChanges.map((row) => row.field || '').filter(Boolean)),
    [draftChanges],
  );
  const pendingAbout = pendingEdits && pendingFields.has('about');
  const pendingPhoto = pendingEdits && pendingFields.has('profile_photo');
  const pendingName =
    pendingEdits &&
    ['full_name', 'name', 'surname', 'patronymic'].some((field) => pendingFields.has(field));
  const pendingTrial = pendingEdits && pendingFields.has('trial_lesson');
  const pendingEdu =
    pendingEdits && [...pendingFields].some((field) => EDU_FIELDS.has(field));
  const pendingServices =
    pendingEdits && [...pendingFields].some((field) => SERVICE_FIELDS.has(field));
  const pendingOnline = pendingEdits && pendingFields.has('format_online');
  const pendingOffline = pendingEdits && pendingFields.has('format_offline');
  const pending = status === 'pending_review';
  const needsChanges = status === 'changes_requested' || status === 'rejected';
  const title = pending
    ? 'Профиль на проверке'
    : needsChanges
      ? 'Нужны изменения'
      : 'Мой профиль';

  return (
    <main className={styles.page}>
      <PageHead crumb={<>Личный кабинет / Профиль</>} title={<>{title}</>} />

      {pending ? (
        <SectionReveal className={styles.card}>
          <h2 className={styles.cardTitle}>Анкета ожидает решения</h2>
          <p className={styles.fact}>
            Анкета на проверке. Одобрение еще не получено.
          </p>
          <div className={styles.row}>
            <p className={styles.rowLabel}>Статус профиля</p>
            <p className={styles.rowValue}>На проверке</p>
          </div>
          <div className={styles.row}>
            <p className={styles.rowLabel}>Публичная карточка</p>
            <p className={styles.rowValue}>Недоступна до одобрения</p>
          </div>
          <div className={styles.row}>
            <p className={styles.rowLabel}>Поиск заявок и чаты</p>
            <p className={styles.rowValue}>Закрыты</p>
          </div>
          <div className={styles.note}>
            <p className={styles.noteTitle}>Следующий шаг</p>
            <CompactText className={styles.noteText} tone={'info'}>
              Дождитесь результата в уведомлениях. Если потребуются изменения, вы
              увидите поля и комментарий.
            </CompactText>
          </div>
        </SectionReveal>
      ) : null}

      {pending ? (
        <SectionReveal className={styles.card}>
          <h2 className={styles.cardTitle}>Лента заявок пока закрыта</h2>
          <p className={styles.fact}>
            Заявки и диалоги не показаны, пока профиль не одобрен.
          </p>
          <div className={styles.note}>
            <p className={styles.noteTitle}>Доступ после одобрения</p>
            <CompactText className={styles.noteText} tone={'info'}>
              Не нужно отправлять анкету повторно только из-за ожидания.
            </CompactText>
          </div>
          <NavLink to='/teacher/edit' className={styles.primary}>
            Продолжить заполнение профиля
          </NavLink>
        </SectionReveal>
      ) : null}

      {pendingEdits && !StudentView ? (
        <SectionReveal className={styles.card}>
          <h2 className={styles.cardTitle}>
            {canRevert ? 'Правки не приняты' : 'Правки на проверке'}
          </h2>
          <p className={styles.pendingNote}>
            {canRevert
              ? 'Модератор не принял правки. Подчеркнутый текст - тот вариант, который вы отправляли. Можно вернуть прежнюю версию.'
              : 'Подчеркнуто то, что появится после проверки. Ученики пока видят прежнюю анкету. Пока проверка идет, поле можно изменить и отправить снова.'}
          </p>
          {canRevert ? (
            <div className={styles.actions}>
              <button
                type="button"
                className={styles.primary}
                disabled={revertDraft.isPending}
                onClick={() => {
                  revertDraft.mutate(undefined, {
                    onSuccess: () =>
                      addNotification('В анкете снова прежняя версия.', 'success'),
                    onError: () =>
                      addNotification('Не удалось вернуть прежнюю версию. Попробуйте еще раз.', 'error'),
                  });
                }}
              >
                Вернуть прежнюю версию
              </button>
            </div>
          ) : null}
        </SectionReveal>
      ) : null}

      {!needsChanges && !pendingEdits && declinedChanges.length > 0 ? (
        <SectionReveal className={styles.card}>
          <h2 className={styles.cardTitle}>Изменения не приняты</h2>
          <CompactText className={styles.fact} tone='warning'>
            Модератор отклонил часть правок. В анкете остались прежние одобренные данные.
          </CompactText>
          {declinedChanges.map((item) => (
            <div key={item.key} className={styles.note}>
              <p className={styles.noteTitle}>{itemTitle(item)}</p>
              <CompactText className={styles.noteText} tone='error'>
                {item.comment}
              </CompactText>
            </div>
          ))}
          <div className={styles.actions}>
            <NavLink to="/teacher/edit" className={styles.primary}>
              Загрузить заново
            </NavLink>
          </div>
        </SectionReveal>
      ) : null}

      {needsChanges ? (
        <SectionReveal className={styles.card}>
          <h2 className={styles.cardTitle}>Комментарий к анкете</h2>
          <p className={styles.fact}>
            {comment?.trim() ||
              'Исправьте отмеченные поля, затем отправьте анкету снова.'}
          </p>
          {rejectedItems.map((item) => (
            <div key={item.key} className={styles.note}>
              <p className={styles.noteTitle}>{itemTitle(item)}</p>
              <CompactText className={styles.noteText} tone='warning'>
                {item.comment?.trim() || 'Нужна правка этого поля.'}
              </CompactText>
            </div>
          ))}
        </SectionReveal>
      ) : null}

      {needsChanges ? (
        <SectionReveal className={styles.card}>
          <h2 className={styles.cardTitle}>После исправлений</h2>
          <div className={styles.row}>
            <p className={styles.rowLabel}>Поиск и чаты</p>
            <p className={styles.rowValue}>Закрыты до одобрения</p>
          </div>
          <p className={styles.about}>
            Сохраните правки и повторно отправьте профиль на проверку. Изменение
            полей само по себе не открывает доступ.
          </p>
          <div className={styles.actions}>
            <NavLink to='/teacher/edit' className={styles.primary}>
              Сохранить исправления
            </NavLink>
            <NavLink to='/teacher/edit' className={styles.ghost}>
              К анкете
            </NavLink>
          </div>
        </SectionReveal>
      ) : null}

      {pending || needsChanges ? null : (
        <ProfileGrid className={styles.gridStart}>
          <SectionReveal className={`${styles.hero} ${profileCardClasses.wide}`}>
            <div className={styles.heroLead}>
              <div className={`${styles.avatar} ${pendingPhoto ? styles.pendingValue : ''}`}>
                <img
                  src={photoPath}
                  alt={fullname || 'Фото профиля'}
                  onError={() => {
                    markProfilePhotoFailed(data?.profile_photo_token);
                    setPhotoFailed(true);
                  }}
                />
              </div>
              <div className={styles.heroCopy}>
                <h2 className={`${styles.name} ${pendingName ? styles.pendingValue : ''}`}>
                  {fullname || 'Репетитор'}
                </h2>
                <PendingNote show={pendingPhoto && !canRevert && !StudentView} to="/teacher/edit#photo" />
                <p className={styles.role}>Репетитор по {subject}</p>
                {!StudentView && data?.id_user ? (
                  <TutorReviewsSummary tutorUserId={data.id_user} />
                ) : null}
                {sex ? <p className={styles.meta}>{sex} пол</p> : null}
                <p className={`${styles.meta} ${pendingTrial ? styles.pendingValue : ''}`}>
                  {data?.trial_lesson
                    ? 'Пробное занятие - по договоренности'
                    : 'Пробное занятие не предлагается'}
                </p>
                <PendingNote show={pendingTrial && !canRevert} to="/teacher/edit#general" />
              </div>
            </div>

            {/* Пустые строки образования не показываем: иначе остается одинокий заголовок без содержимого. */}
            {data?.teacher_educations &&
            data.teacher_educations.some((row) =>
              [row.university, row.faculty, row.specialization].some(Boolean),
            ) ? (
              <>
                <div className={styles.rule} />
                <div>
                  <p className={styles.eduTitle}>Образование</p>
                  {data.teacher_educations.map((row, index) => {
                    const edu = [row.university, row.faculty, row.specialization]
                      .filter(Boolean)
                      .join(', ');
                    if (!edu) return null;
                    return (
                      <p
                        key={`education-${index}`}
                        className={`${styles.eduLine} ${pendingEdu ? styles.pendingValue : ''}`}
                      >
                        {edu}
                      </p>
                    );
                  })}
                </div>
                <PendingNote show={pendingEdu && !canRevert} to="/teacher/edit#education" />
              </>
            ) : null}

            {!StudentView ? (
              <NavLink to='/teacher/edit' className={styles.editAction}>
                <Pencil size={18} strokeWidth={2.2} aria-hidden />
                <span>Редактировать профиль</span>
              </NavLink>
            ) : null}
          </SectionReveal>

          {!StudentView ? <TutorDailyResponsesCard /> : null}

          {data?.teacher_prices && data.teacher_prices.length > 0 ? (
            <SectionReveal className={styles.card}>
              <ProfileCardTitle className={styles.cardTitle} icon={<CaseIcon />}>Услуги и стоимость</ProfileCardTitle>
              {/* design-fix 2026-10-09: строки услуг одной высоты (обёртка-сетка) */}
              <div className={styles.servicesGrid}>
              {data.teacher_prices.map((row, i) => (
                <div key={`service-${i}`} className={styles.service}>
                  <div className={styles.serviceCopy}>
                    {/* Figma 3:30077: жирным - направление, ниже серым - предмет. */}
                    <p className={`${styles.serviceName} ${pendingServices ? styles.pendingValue : ''}`}>
                      {row.subcategory_title ||
                        row.subcategory_name ||
                        row.category_title ||
                        row.category_name ||
                        'Математика'}
                    </p>
                    {row.subcategory_title || row.subcategory_name ? (
                      <p className={styles.serviceSub}>
                        {row.category_title || row.category_name || 'Математика'}
                      </p>
                    ) : null}
                  </div>
                  <p className={`${styles.servicePrice} ${pendingServices ? styles.pendingValue : ''}`}>
                    {formatPrice(row.price)}
                  </p>
                </div>
              ))}
              </div>
              <PendingNote show={pendingServices && !canRevert && !StudentView} to="/teacher/edit#services" />
              {!StudentView ? (
                <NavLink to='/teacher/edit' className={styles.link}>
                  Редактировать услуги
                </NavLink>
              ) : null}
            </SectionReveal>
          ) : null}

          {hasAboutContent(data?.about) ? (
            <SectionReveal className={`${styles.card} ${profileCardClasses.wide}`}>
              <ProfileCardTitle className={styles.cardTitle} icon={<UserIcon />}>О себе</ProfileCardTitle>
              <div className={`${styles.about} ${pendingAbout ? styles.pendingValue : ''}`}>
                <RichAboutContent content={data?.about} />
              </div>
              <PendingNote show={pendingAbout && !canRevert && !StudentView} to="/teacher/edit#general" />
              {!StudentView ? (
                <NavLink to='/teacher/edit' className={styles.link}>
                  Редактировать описание
                </NavLink>
              ) : null}
            </SectionReveal>
          ) : null}

          {data?.format_online || data?.format_offline ? (
            <SectionReveal className={styles.card}>
              <ProfileCardTitle className={styles.cardTitle} icon={<CalendarIcon />}>Проведение урока</ProfileCardTitle>
              <div className={styles.formats}>
                {data.format_online ? (
                  <div className={`${styles.format} ${pendingOnline ? styles.pendingValue : ''}`}>
                    <Video className={styles.formatIcon} aria-hidden='true' />
                    <p className={styles.formatTitle}>Онлайн</p>
                    <p className={styles.formatText}>
                      Видеосвязь и общая доска. Платформу согласуем перед занятием.
                    </p>
                  </div>
                ) : null}
                {data.format_offline ? (
                  <div className={`${styles.format} ${pendingOffline ? styles.pendingValue : ''}`}>
                    <MapPin className={styles.formatIcon} aria-hidden='true' />
                    <p className={styles.formatTitle}>Очно</p>
                    <p className={styles.formatText}>
                      Место и время согласуем в чате. Доступность зависит от адреса.
                    </p>
                  </div>
                ) : null}
              </div>
              <PendingNote
                show={(pendingOnline || pendingOffline) && !canRevert && !StudentView}
                to="/teacher/edit#general"
              />
              {!StudentView ? (
                <NavLink to='/teacher/edit' className={styles.link}>
                  Редактировать формат
                </NavLink>
              ) : null}
            </SectionReveal>
          ) : null}

          {!StudentView && data?.id_user ? (
            <SectionReveal className={`${styles.card} ${profileCardClasses.wide}`}>
              <TutorReviewsSection tutorUserId={data.id_user} showPendingBadge />
            </SectionReveal>
          ) : null}
        </ProfileGrid>
      )}
    </main>
  );
};
